import { useEffect } from "react";
import { useLocation } from "@tanstack/react-router";

const HEADER_OFFSET = 96;
const STORE_KEY = "vbc:anchor-restore";

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true
  );
}

/** Stable id for the current history entry so back/forward can be told apart. */
function entryKey(): string {
  if (typeof window === "undefined") return "";
  const key = (window.history.state as { key?: string } | null)?.key;
  return `${key ?? "nokey"}|${window.location.pathname}${window.location.search}${window.location.hash}`;
}

type Restore = { y: number; focusId?: string };

function readStore(): Record<string, Restore> {
  if (typeof sessionStorage === "undefined") return {};
  try {
    return JSON.parse(sessionStorage.getItem(STORE_KEY) ?? "{}") as Record<string, Restore>;
  } catch {
    return {};
  }
}

function writeStore(store: Record<string, Restore>) {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(STORE_KEY, JSON.stringify(store));
  } catch {
    /* storage full or blocked — restoration is best-effort */
  }
}

function saveEntry(patch: Partial<Restore>) {
  const key = entryKey();
  if (!key) return;
  const store = readStore();
  store[key] = { ...store[key], ...patch, y: patch.y ?? window.scrollY };
  writeStore(store);
}

function loadEntry(): Restore | undefined {
  const key = entryKey();
  return key ? readStore()[key] : undefined;
}

/**
 * True while the browser is processing a back/forward navigation, so hash
 * handling can restore the previous position instead of re-animating a jump.
 */
let popNavigation = false;

/** True while a back/forward restore is re-applying an offset, so the scroll
 * listener does not overwrite the saved position with an interim value. */
let restoring = false;

if (typeof window !== "undefined") {
  // Own scroll behaviour ourselves; the browser's guess fights our offsets.
  if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";

  window.addEventListener("popstate", () => {
    popNavigation = true;
    // Cleared after the route/effects for this entry have run.
    setTimeout(() => {
      popNavigation = false;
    }, 400);
  });

  // Persist position (and the anchor we focused) before leaving an entry.
  const persist = () => saveEntry({});
  window.addEventListener("pagehide", persist);
  window.addEventListener("beforeunload", persist);
  let raf = 0;
  window.addEventListener(
    "scroll",
    () => {
      if (restoring || raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        saveEntry({});
      });
    },
    { passive: true },
  );
}

/** Shared polite live region: some browsers do not announce programmatic focus. */
function announce(message: string) {
  if (typeof document === "undefined" || !message) return;
  let region = document.getElementById("anchor-announcer");
  if (!region) {
    region = document.createElement("div");
    region.id = "anchor-announcer";
    region.setAttribute("role", "status");
    region.setAttribute("aria-live", "polite");
    region.className = "sr-only";
    document.body.appendChild(region);
  }
  // Clear first so repeat jumps to the same section are still announced.
  region.textContent = "";
  window.setTimeout(() => {
    region!.textContent = message;
  }, 50);
}

/** Text a screen reader should hear when landing on the anchor target. */
function destinationLabel(el: HTMLElement): string {
  const labelled = el.getAttribute("aria-labelledby");
  const labelSource = labelled ? document.getElementById(labelled) : null;
  const heading =
    el.matches("h1,h2,h3,h4,h5,h6") ? el : (labelSource ?? el.querySelector("h1,h2,h3,h4,h5,h6"));
  const text = (el.getAttribute("aria-label") || heading?.textContent || "").trim();
  return text ? `${text}. Section` : "";
}

/**
 * Scrolls an anchor target into view and moves keyboard focus to it so screen
 * readers and tab order continue from the destination section.
 */
export function focusAnchor(hash: string, opts?: { smooth?: boolean; silent?: boolean }): boolean {
  if (typeof document === "undefined") return false;
  const id = hash.replace(/^#/, "");
  if (!id) return false;
  const el = document.getElementById(id);
  if (!el) return false;

  const smooth = opts?.smooth ?? !prefersReducedMotion();
  const scrollToTarget = (behavior: ScrollBehavior) => {
    const top = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
    window.scrollTo({ top: Math.max(top, 0), behavior });
  };
  scrollToTarget(smooth ? "smooth" : "auto");

  // Images and lazy sections above the target can settle after the scroll
  // starts, which leaves the heading off by the shifted amount. Re-check a
  // few times and correct without re-animating; stop as soon as the visitor
  // takes over scrolling.
  let corrections = 0;
  let aborted = false;
  const abort = () => {
    aborted = true;
    window.removeEventListener("wheel", abort);
    window.removeEventListener("touchstart", abort);
    window.removeEventListener("keydown", abort);
  };
  window.addEventListener("wheel", abort, { passive: true });
  window.addEventListener("touchstart", abort, { passive: true });
  window.addEventListener("keydown", abort);
  const correct = () => {
    // A route change detaches the node; stale timers must not scroll the new page.
    if (aborted || !el.isConnected) return abort();
    const delta = el.getBoundingClientRect().top - HEADER_OFFSET;
    if (Math.abs(delta) > 2) scrollToTarget("auto");
    if (corrections++ < 6) window.setTimeout(correct, 150);
    else abort();
  };
  window.setTimeout(correct, smooth ? 500 : 60);




  // Headings and sections are not focusable by default; -1 keeps them out of
  // tab order while still allowing focus to move there.
  if (!el.hasAttribute("tabindex")) el.setAttribute("tabindex", "-1");
  el.classList.add("outline-none");
  // Focus without a second, competing scroll jump.
  el.focus({ preventScroll: true });
  if (!opts?.silent) announce(destinationLabel(el));
  saveEntry({ focusId: id });
  return true;
}

/**
 * Applies scrolling + focus after navigation (including cross-page jumps).
 * On back/forward navigations it restores the saved scroll offset and the
 * anchor that previously held focus, without smooth animation.
 */
export function useHashFocus() {
  const location = useLocation({ select: (l) => `${l.pathname}${l.hash}` });
  const hash = useLocation({ select: (l) => l.hash });

  useEffect(() => {
    const isPop = popNavigation;
    const saved = isPop ? loadEntry() : undefined;

    let frames = 0;
    let raf = 0;
    let timer = 0;

    const attempt = () => {
      if (saved) {
        const el = saved.focusId ? document.getElementById(saved.focusId) : null;
        if (el) {
          if (!el.hasAttribute("tabindex")) el.setAttribute("tabindex", "-1");
          el.focus({ preventScroll: true });
        }
        // Restoring an exact offset beats recomputing the anchor position,
        // which can differ once images and lazy sections have settled.
        restoring = true;
        window.scrollTo({ top: saved.y, behavior: "auto" });
        if (frames++ < 5) {
          raf = requestAnimationFrame(attempt);
          return;
        }
        // The page often keeps growing (fonts, images, lazy sections) past the
        // first frames, so keep re-applying the offset briefly before letting
        // scroll tracking resume.
        if (frames < 20) {
          timer = window.setTimeout(attempt, 100);
          frames++;
          return;
        }
        restoring = false;
        return;
      }

      if (hash) {
        if (focusAnchor(hash, { smooth: !isPop && !prefersReducedMotion() })) return;
      } else if (!isPop) {
        return;
      }
      if (frames++ < 30) raf = requestAnimationFrame(attempt);
    };

    raf = requestAnimationFrame(attempt);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(timer);
      restoring = false;
    };
  }, [location, hash]);

}
